// TREGUESIT FINANCIAR
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { useMediaQuery, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useQueryParams, NumberParam, StringParam, DelimitedNumericArrayParam } from 'use-query-params';

import SectionWithArrows from 'components/organisms/SectionWithArrows/SectionWithArrows';
import { FinancialIndicatorsService } from 'services';

import { StatisticsIndicatorsContext } from './contexts';
import { Hero, StatisticsSelectWrapper } from './components';
import clsx from 'clsx';
import { ActionButton } from 'components/atoms';
import TrendLinesChart from './components/TrendLinesChart';
import Loader from 'components/organisms/Loader';

const useStyles = makeStyles((theme) => ({
  root: {},
  content: {
    minHeight: 450,
    '& .section': {
      paddingTop: theme.spacing(4),
    },
  },
  filtersContainer: {
    marginBottom: theme.spacing(1),
  },
  chartContainer: {
    boxShadow: '0 0 4px #00000029',
    borderRadius: '10px',
    marginBottom: '20px',
    marginTop: '20px',
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  rightContainer: {
    width: '250px',
  },
  leftContainer: {
    width: '75%',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
  divider: {
    height: 5,
    marginTop: 50,
    marginBottom: 30,
  },
  dictionary: {
    boxShadow: '0px 3px 6px #00000029',
    borderRadius: '10px',
    backgroundColor: '#f6f6f6',
    padding: theme.spacing(4),
    marginTop: theme.spacing(6),
    width: '280px',
  },
  dictionaryTitle: {
    marginBottom: 0,
    alignSelf: 'flex-end',
  },
  dictionaryHeader: {
    display: ' flex',
    flexDirection: 'row',
    marginBottom: 20,
  },
  dictionaryTerm: {
    fontStyle: 'italic',
    fontSize: 12,
  },
  dictionaryParagraph: {
    display: 'inline',
    marginBottom: 10,
    fontSize: 12,
  },
  filterText: {
    fontSize: '18px',
    marginLeft: '12px',
    marginBottom: '10px',
  },
  title: {
    fontSize: '24px',
    fontStyle: 'Roboto-Regular',
    textAlign: 'center',
    color: '#981B49',
  },
  chartButton: {
    width: '282px',
    fontWeight: theme.typography.fontWeightBold,
    fontSize: 15,
    marginTop: theme.spacing(5),
    color: '#981B49',
    backgroundColor: 'white',
    boxShadow: '0px 3px 6px #00000029',
    '&:hover': {
      color: 'white',
      backgroundColor: 'black',
    },
  },
}));

const StatisticsIndicators = (props) => {
  const { className, ...rest } = props; // eslint-disable-line no-unused-vars
  const classes = useStyles();
  const { t } = useTranslation();
  const financialIndicatorsService = new FinancialIndicatorsService();

  const [ setFilterTypes] = useState('municipality');
  const [governmentEntities, setGovernmentEntities] = useState([]);
  const [years, setYears] = useState([]);
  const [chartTitle, setChartTitle] = useState('');
  const [chartData, setChartData] = useState([]);
  const [chartTypePercentage, setChartTypePercentage] = useState(false);
  const [requiredFilters, setRequiredFilters] = useState({
    municipalityError: false,
    yearError: false,
  });

  const [filters, setFilters] = useQueryParams({
    year: DelimitedNumericArrayParam,
    municipality: DelimitedNumericArrayParam,
    filterType: StringParam,
    formulaId: NumberParam,
    formulaName: StringParam,
    module: StringParam,
  });
  const [spinner, setSpinner] = useState(true);
  const [isEmpty, setIsEmpty] = useState(false);

  const prevFormulaNameRef = useRef();
  const prevFormulaIdRef = useRef();
  const prevModuleRef = useRef();

  const theme = useTheme();
  // eslint-disable-next-line no-unused-vars
  const isMd = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  });

  useEffect(() => {
    setIsEmpty(false);
    setSpinner(true);
    let defaultFilters = { ...filters };
    if (!filters.year || !filters.municipality || !filters.module || !filters.formulaId) {
      defaultFilters = {
        formulaName: 'Të ardhurat e veta ndaj totalit të të ardhurave vendore',
        filterType: 'municipality',
        formulaId: 336,
        module: 'localAutonomy',
        municipality: [-1],
        year: [28, 29, 30, 31, 32, 33, 34, 35],
      };
    }
    setFilters(defaultFilters);
    setFilterTypes('municipality');

    (async () => {
      const { data: governmentEntitiesData } = await financialIndicatorsService.governmentEntities();
      setGovernmentEntities(governmentEntitiesData);
    })();

    (async () => {
      const { data: yearsData } = await financialIndicatorsService.years();
      setYears(yearsData);
    })();

    (async () => {
      const { data } = await financialIndicatorsService.getChartData(
        defaultFilters.module,
        defaultFilters.filterType,
        defaultFilters,
      );
      if (data?.values.length < 1) setIsEmpty(true);
      setChartData(data);
      setChartTypePercentage(data.percentage);
    })();
  }, []);

  useEffect(() => {
    prevFormulaNameRef.current = filters.formulaName;
  }, [filters.formulaName]);

  // Each time formulaId/module changes
  useEffect(() => {
    prevFormulaIdRef.current = filters.formulaId;
    prevModuleRef.current = filters.module;
  }, [filters.formulaId, filters.module]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterChange = (filter) => ({ target: { value }, selectedItem }) => {
    setRequiredFilters({
      municipalityError: false,
      yearError: false,
      periodError: false,
      dataLevelsError: false,
    });
    setFilterTypes('municipality');

    // if it is a single value from simple select
    if (['municipality', 'year'].includes(filter) && !Array.isArray(value)) value = [value];
    setFilters((prev) => ({ ...prev, [filter]: value }));
  };

  const buildChart = () => {
    setIsEmpty(false);
    setSpinner(true);
    if (!filters.year || !filters.municipality || !filters.module || !filters.formulaId) {
      setRequiredFilters({
        municipalityError: !filters.municipality,
        yearError: !filters.year,
      });
      return;
    }

    setFilters({ ...filters, formulaName: chartTitle });

    (async () => {
      const { data } = await financialIndicatorsService.getChartData(filters.module, filters.filterType, filters);
      if (data?.values.length < 1) setIsEmpty(true);
      setChartData(data);
      setChartTypePercentage(data.percentage);
    })();
  };

  useEffect(() => {
    if (chartData.values.length > 0 || isEmpty) setSpinner(false);
  }, [chartData]);

  return (
    <StatisticsIndicatorsContext.Provider
      value={{
        filters,
        setChartTitle,
        setFilters: (value) => {
          setFilters(value);
        },
      }}
    >
      {spinner ? (
        <Loader />
      ) : (
        <div className={clsx(classes.root, className)}>
          <SectionWithArrows className={clsx(classes.content)} statisticsWidth={true}>
            <div className={classes.container}>
              <div className={classes.leftContainer}>
                <>
                  {chartTypePercentage ? (
                    <div className={classes.title}>
                      {filters.formulaName}
                      {/*{t('FINANCIAL_STATISTICS.SUB_TITLE_PERCENTAGE', { title: filters.formulaName })}*/}
                    </div>
                  ) : (
                    <div className={classes.title}>
                      {filters.formulaName}
                      {/*{t('FINANCIAL_STATISTICS.SUB_TITLE', { title: filters.formulaName })}*/}
                    </div>
                  )}
                  <div className={classes.chartContainer}>
                    {chartData['values'].length < 1 ? (
                      <h3>{t('STATISTICS.NO_DATA')}</h3>
                    ) : (
                      <TrendLinesChart data={chartData} chartTypePercentage={chartTypePercentage} />
                    )}
                  </div>
                </>
              </div>
              <div className={classes.rightContainer}>
                <div className={classes.filterText}>{t('STATISTICS.STATISTIC_INDICATOR_FILTER')}</div>
                <Hero />
                <div className={classes.title}>{t('STATISTICS.FILTER_TYPE')}</div>
                <Grid container>
                  <Grid item xs={12} lg={12} className={clsx(classes.filtersContainer)}>
                    <StatisticsSelectWrapper
                      name='municipality'
                      placeholder={t('STATISTICS.MUNICIPALITY')}
                      startText={t('STATISTICS.MUNICIPALITY')}
                      items={governmentEntities}
                      value={filters.municipality}
                      onChange={handleFilterChange('municipality')}
                      multiple={true}
                      fromStatistics={true}
                      icon={'Statistics/Bashkite'}
                      imageName={'Bashkia'}
                      error={requiredFilters.municipalityError}
                    />
                  </Grid>
                  <Grid item xs={12} lg={12} className={clsx(classes.filtersContainer)}>
                    <StatisticsSelectWrapper
                      name='year'
                      placeholder={t('COMMON.YEAR')}
                      startText={t('COMMON.YEAR')}
                      items={years}
                      value={filters.year}
                      onChange={handleFilterChange('year')}
                      multiple={true}
                      fromStatistics={true}
                      icon={'Statistics/TeArdhurat'}
                      imageName={'Viti'}
                      error={requiredFilters.yearError}
                    />
                  </Grid>
                  <Grid item xs={12} lg={12} className={clsx(classes.filtersContainer)}>
                    <ActionButton
                      variant='contained'
                      size='small'
                      className={clsx(classes.chartButton)}
                      onClick={buildChart}
                    >
                      {t('STATISTICS.BUILD_CHART')}
                    </ActionButton>
                  </Grid>
                </Grid>
              </div>
            </div>
          </SectionWithArrows>
        </div>
      )}
    </StatisticsIndicatorsContext.Provider>
  );
};

StatisticsIndicators.propTypes = {
  /**
   * External classes
   */
  className: PropTypes.string,
};

export default StatisticsIndicators;
